// @flow
import React from 'react';
import { SectionedModal } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { ModalConfirm } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Banner,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import { MiniProfileModalActions } from '../../../ResidentProfile/MiniProfileBridge';
import PetFeeModal from '../../../EditResidentHousehold/PetFeeModal';
import editResidentHouseholdMessages from '../../../EditResidentHousehold/messages';
import appMessages from '../../../App/messages';
import usePetModal from './hooks/usePetModal';
import messages from './messages';
import type { PetViewModel } from '../types';

// A form-wide lock outranks the pet-rent notice; unsupported outranks both.
const buildHeaderBanner = ({
  isUnsupportedPet,
  unsupportedPetText,
  isLockedByOpenCerts,
  openCertHelperText,
  showPetRentWarning,
  petRentChargeWarning,
}) => {
  if (isUnsupportedPet) {
    return { status: 'warning', message: unsupportedPetText };
  }
  if (isLockedByOpenCerts) {
    return { status: 'warning', message: openCertHelperText };
  }
  if (showPetRentWarning) {
    return { status: 'warning', message: petRentChargeWarning };
  }
  return undefined;
};

type Props = {
  organizationId: string,
  propertyId: string,
  residentId: ?string,
  // Applicant writes require the application ID and complete applicant list.
  applicationId?: ?string,
  applicants?: ?Array<Object>,
  isApplicantSubject?: boolean,
  // An absent pet selects add mode. Denied pets cannot open this modal.
  pet?: ?PetViewModel,
  // An open certification freezes the household: no add, edit, or delete.
  hasOpenCerts?: boolean,
  // Deleting a pet requires the resident-delete permission.
  canDelete?: boolean,
  onClose: () => void,
  onSaveSuccess?: ?() => void,
};

const PetModal = ({
  organizationId,
  propertyId,
  residentId,
  applicationId = null,
  applicants = null,
  isApplicantSubject = false,
  pet = null,
  hasOpenCerts = false,
  canDelete = false,
  onClose,
  onSaveSuccess = null,
}: Props) => {
  const {
    formatMessage,
    title,
    isNewPet,
    isMutating,
    isBusy,
    isLockedByOpenCerts,
    openCertHelperText,
    isUnsupportedPet,
    unsupportedPetText,
    ReactHookFormProps,
    sections,
    confirmationOpen,
    canDeletePet,
    handleSave,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    petFeeModal,
    isFeeModalOpen,
    handleFeeModalConfirm,
    handleFeeModalCancel,
    petRentConfigured,
    leaseMonthlyPetRent,
    showPetRentWarning,
    petNameForCharges,
    intl,
  } = usePetModal({
    organizationId,
    propertyId,
    residentId,
    applicationId,
    applicants,
    isApplicantSubject,
    pet,
    hasOpenCerts,
    canDelete,
    onClose,
    onSaveSuccess,
  });

  // Pending pets open read-only; every other pet here is editable.
  const actionsProps = [
    {
      children: formatMessage(appMessages.cancel),
      variant: 'text',
      onClick: onClose,
      disabled: isMutating,
      'data-testid': 'pet-modal-cancel',
    },
    {
      children: formatMessage(appMessages.save),
      onClick: handleSave,
      // Also wait for charge data that controls the fee modal.
      disabled: isBusy || isLockedByOpenCerts || isUnsupportedPet,
      'data-testid': 'pet-modal-save',
    },
  ];

  // Deleting a pet permanently removes its Applicant record, so it is offered
  // only with the resident-delete permission and only for a saved pet that
  // carries the Applicant ID the delete route addresses.
  const deleteActionProps =
    isNewPet || !canDeletePet
      ? null
      : {
          children: formatMessage(messages.delete),
          onClick: handleDeleteClick,
          disabled: isMutating || isLockedByOpenCerts,
          // MiniProfileModalActions renders a tooltip only when non-empty.
          tooltip: isLockedByOpenCerts
            ? formatMessage(messages.deleteOpenCertTooltip)
            : '',
          'data-testid': 'pet-modal-delete',
        };

  return (
    <>
      <SectionedModal
        // Keep the form mounted while a child modal is open to preserve values.
        open={!confirmationOpen && !isFeeModalOpen}
        title={title}
        onClose={onClose}
        sections={sections}
        ReactHookFormProps={ReactHookFormProps}
        actions={
          <MiniProfileModalActions
            deleteActionProps={deleteActionProps}
            actionsProps={actionsProps}
          />
        }
        headerBanner={buildHeaderBanner({
          isUnsupportedPet,
          unsupportedPetText,
          isLockedByOpenCerts,
          openCertHelperText,
          showPetRentWarning,
          petRentChargeWarning: formatMessage(
            editResidentHouseholdMessages.petRentChargeWarning,
          ),
        })}
      />
      <PetFeeModal
        open={isFeeModalOpen}
        handleSubmit={handleFeeModalConfirm}
        onCancel={handleFeeModalCancel}
        // Prevent duplicate one-time charges.
        disabled={isMutating}
        petsToCharge={[{ name: petNameForCharges }]}
        petRentConfigured={petRentConfigured}
        leaseMonthlyPetRent={leaseMonthlyPetRent}
        intl={intl}
        reactHookFormProps={petFeeModal.reactHookFormProps}
        formFields={petFeeModal.formFields}
      />
      <ModalConfirm
        open={confirmationOpen}
        title={formatMessage(messages.deleteConfirmTitle)}
        onClose={handleDeleteCancel}
        ContentProps={{
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 565,
            },
          },
        }}
        actionsProps={[
          {
            children: formatMessage(appMessages.cancel),
            variant: 'text',
            onClick: handleDeleteCancel,
            disabled: isMutating,
            'data-testid': 'pet-delete-cancel',
          },
          {
            children: formatMessage(appMessages.confirm),
            onClick: handleDeleteConfirm,
            disabled: isMutating,
            'data-testid': 'pet-delete-confirm',
          },
        ]}
      >
        <Banner
          status="error"
          message={formatMessage(messages.deleteConfirmBanner)}
          sx={{ width: '100%', mb: 2 }}
        />
        <Typography variant="bodyMedium">
          {formatMessage(messages.deleteConfirmBody, {
            name: pet?.name ?? '',
          })}
        </Typography>
      </ModalConfirm>
    </>
  );
};

export default PetModal;
